// Test-report generator UI — admin form that posts to
// /api/v1/admin/test-reports.  Backend enqueues a print job of kind
// 'test-report'; this page redirects to /print (history) on success.

import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import { ErrorBanner } from "@/components/ErrorBanner";
import { PageHeader } from "@/components/PageHeader";
import { RequirePermission } from "@/components/RequirePermission";
import { request } from "@/lib/api";
import { useSystemSettings } from "@/lib/system";
import type { TestReportPrefill } from "@/lib/types";

const INPUT = "w-full px-3 py-2 rounded-md bg-surface border border-border text-sm";
const INPUT_MONO = `${INPUT} font-mono`;

const ACCESS_KEY = "mes:auth:access";

interface SavedReport {
  id: string;
  params: Record<string, number | string>;
  meta: Record<string, string | undefined>;
  serials: string[];
}

interface FormShape {
  article: string;
  serials: string; // raw textarea, parsed on submit
  production_date: string;
  min_flow: string;
  nominal_flow: string;
  flow_tolerance: string;
  min_selectivity: string;
  nominal_selectivity: string;
  test_pressure: string;
  salt_nacl: string;
  test_temperature: string;
  recovery: string;
  ph: string;
  max_working_pressure: string;
  max_temperature: string;
  free_chlorine_max: string;
  active_area: string;
  salt: string;
  customer: string;
  doc_number: string;
  doc_date: string;
  doc_standard: string;
  issuer: string;
  issuer_contact: string;
}

const DEFAULTS: FormShape = {
  article: "ECO-RO-400",
  serials: ["260500" + "0001"].join("\n"),
  production_date: new Date().toISOString().slice(0, 10),
  min_flow: "39.0",
  nominal_flow: "45.0",
  // Отраслевой допуск на элемент. Из него выводится порог годности, когда
  // минимум не задан явно.
  flow_tolerance: "15",
  min_selectivity: "99.50",
  nominal_selectivity: "99.80",
  test_pressure: "15.5",
  salt_nacl: "2000",
  test_temperature: "25",
  recovery: "15",
  ph: "7.0",
  max_working_pressure: "41",
  max_temperature: "45",
  free_chlorine_max: "0.1",
  active_area: "37",
  salt: "",
  // Получатель — заполняется вручную под конкретного заказчика; ничего
  // не подставляем по умолчанию, чтобы никакая чужая инфа никогда не
  // оказалась в PDF.  Пустое поле = блок «Получатель» в PDF не рисуется.
  customer: "",
  doc_number: "EE-2026-0001",
  doc_date: new Date().toISOString().slice(0, 10),
  doc_standard: "EN 10204 type 2.2",
  // Эмитент — из профиля компании (см. эффект ниже).
  issuer: "",
  issuer_contact: "",
};

export default function TestReportForm() {
  return (
    <RequirePermission codes="document.test_report.manage">
      <Inner />
    </RequirePermission>
  );
}

function Inner() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const editId = params.get("from");
  // ?manufacturing_order_id=:id — автозаполнение из заказа на производство
  // (артикул, целевые параметры, серийники партии). Взаимоисключимо с ?from=.
  const moId = !editId ? params.get("manufacturing_order_id") : null;

  const [f, setF] = useState<FormShape>(DEFAULTS);
  const [hydrated, setHydrated] = useState(!editId && !moId);

  const company = useSystemSettings().data?.company;
  useEffect(() => {
    if (!company) return;
    setF((prev) =>
      prev.issuer
        ? prev
        : { ...prev, issuer: company.name, issuer_contact: prev.issuer_contact || (company.contact_email ?? "") },
    );
  }, [company]);

  // When ?from=:id is set, hydrate the form from the saved row.
  const loaded = useQuery({
    queryKey: ["admin", "test-reports", "get", editId],
    queryFn: () => request<SavedReport>(`/admin/test-reports/${editId}`),
    enabled: !!editId,
  });

  // When ?manufacturing_order_id=:id is set, prefill from the order context.
  const prefill = useQuery({
    queryKey: ["admin", "test-reports", "prefill", moId],
    queryFn: () =>
      request<TestReportPrefill>(
        `/admin/test-reports/prefill?manufacturing_order_id=${moId}`,
      ),
    enabled: !!moId,
  });

  useEffect(() => {
    if (!prefill.data || hydrated) return;
    const p = prefill.data.params ?? {};
    const num = (v: unknown, fb: string) =>
      typeof v === "number" || typeof v === "string" ? String(v) : fb;
    setF((prev) => ({
      ...prev,
      article: prefill.data!.article ?? prev.article,
      serials: prefill.data!.serials.join("\n"),
      min_flow: num(p.min_flow, prev.min_flow),
      nominal_flow: num(p.nominal_flow, prev.nominal_flow),
      flow_tolerance: num(p.flow_tolerance, prev.flow_tolerance),
      min_selectivity: num(p.min_selectivity, prev.min_selectivity),
      nominal_selectivity: num(p.nominal_selectivity, prev.nominal_selectivity),
      test_pressure: num(p.test_pressure, prev.test_pressure),
      salt_nacl: num(p.salt_nacl, prev.salt_nacl),
      test_temperature: num(p.test_temperature, prev.test_temperature),
      recovery: num(p.recovery, prev.recovery),
      ph: num(p.ph, prev.ph),
      max_working_pressure: num(p.max_working_pressure, prev.max_working_pressure),
      max_temperature: num(p.max_temperature, prev.max_temperature),
      free_chlorine_max: num(p.free_chlorine_max, prev.free_chlorine_max),
      active_area: num(p.active_area, prev.active_area),
    }));
    setHydrated(true);
  }, [prefill.data, hydrated]);

  useEffect(() => {
    if (!loaded.data || hydrated) return;
    const p = loaded.data.params;
    const m = loaded.data.meta;
    const num = (v: unknown, fb: string) =>
      typeof v === "number" || typeof v === "string" ? String(v) : fb;
    setF({
      article: num(p.article, DEFAULTS.article),
      serials: loaded.data.serials.join("\n"),
      production_date: num(p.production_date, DEFAULTS.production_date),
      min_flow: num(p.min_flow, DEFAULTS.min_flow),
      nominal_flow: num(p.nominal_flow, DEFAULTS.nominal_flow),
      flow_tolerance: num(p.flow_tolerance, DEFAULTS.flow_tolerance),
      min_selectivity: num(p.min_selectivity, DEFAULTS.min_selectivity),
      nominal_selectivity: num(p.nominal_selectivity, DEFAULTS.nominal_selectivity),
      test_pressure: num(p.test_pressure, DEFAULTS.test_pressure),
      salt_nacl: num(p.salt_nacl, DEFAULTS.salt_nacl),
      test_temperature: num(p.test_temperature, DEFAULTS.test_temperature),
      recovery: num(p.recovery, DEFAULTS.recovery),
      ph: num(p.ph, DEFAULTS.ph),
      max_working_pressure: num(p.max_working_pressure, DEFAULTS.max_working_pressure),
      max_temperature: num(p.max_temperature, DEFAULTS.max_temperature),
      free_chlorine_max: num(p.free_chlorine_max, DEFAULTS.free_chlorine_max),
      active_area: num(p.active_area, DEFAULTS.active_area),
      salt: m.salt ?? "",
      customer: m.customer ?? "",
      doc_number: m.doc_number ?? "",
      doc_date: m.doc_date ?? DEFAULTS.doc_date,
      doc_standard: m.doc_standard ?? DEFAULTS.doc_standard,
      issuer: m.issuer ?? DEFAULTS.issuer,
      issuer_contact: m.issuer_contact ?? DEFAULTS.issuer_contact,
    });
    setHydrated(true);
  }, [loaded.data, hydrated]);

  const m = useMutation({
    mutationFn: async (): Promise<void> => {
      const serials = f.serials
        .replace(/,/g, "\n")
        .split("\n")
        .map((s) => s.trim())
        .filter((s) => s.length > 0);
      if (serials.length === 0) throw new Error(t("testReportForm.errNoSerials"));

      const body = {
        params: {
          article: f.article.trim(),
          min_flow: Number(f.min_flow),
          nominal_flow: Number(f.nominal_flow),
          // Пустое поле не шлём: допуск необязателен, а Number('') = 0 схема
          // отвергнет (positive), уронив создание протокола.
          ...(f.flow_tolerance.trim() !== ""
            ? { flow_tolerance: Number(f.flow_tolerance) }
            : {}),
          min_selectivity: Number(f.min_selectivity),
          nominal_selectivity: Number(f.nominal_selectivity),
          test_pressure: Number(f.test_pressure),
          salt_nacl: Number(f.salt_nacl),
          test_temperature: Number(f.test_temperature),
          recovery: Number(f.recovery),
          ph: Number(f.ph),
          max_working_pressure: Number(f.max_working_pressure),
          max_temperature: Number(f.max_temperature),
          free_chlorine_max: Number(f.free_chlorine_max),
          active_area: Number(f.active_area),
          production_date: f.production_date.trim(),
        },
        serials,
        meta: {
          customer: f.customer.trim(),
          doc_number: f.doc_number.trim(),
          doc_date: f.doc_date.trim(),
          doc_standard: f.doc_standard.trim(),
          issuer: f.issuer.trim(),
          issuer_contact: f.issuer_contact.trim(),
          salt: f.salt.trim() || undefined,
        },
      };

      const token =
        typeof localStorage !== "undefined" ? localStorage.getItem(ACCESS_KEY) : null;

      // Edit mode: PATCH the saved row (returns JSON, no PDF), then
      // call /render to get the fresh PDF.
      // Create mode: POST the new row + receive the PDF inline.
      if (editId) {
        const patchRes = await fetch(`/api/v1/admin/test-reports/${editId}`, {
          method: "PATCH",
          headers: {
            "content-type": "application/json",
            ...(token ? { authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify(body),
        });
        if (!patchRes.ok) {
          let msg = `HTTP ${patchRes.status}`;
          try {
            const text = await patchRes.text();
            const j = text ? JSON.parse(text) : null;
            msg = j?.error?.message ?? text ?? msg;
          } catch {
            /* ignore */
          }
          throw new Error(msg);
        }
        const renderRes = await fetch(`/api/v1/admin/test-reports/${editId}/render`, {
          method: "POST",
          headers: { ...(token ? { authorization: `Bearer ${token}` } : {}) },
        });
        if (!renderRes.ok) throw new Error(`render HTTP ${renderRes.status}`);
        const blob = await renderRes.blob();
        const url = URL.createObjectURL(blob);
        window.open(url, "_blank", "noopener");
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
        return;
      }

      const res = await fetch("/api/v1/admin/test-reports", {
        method: "POST",
        headers: {
          "content-type": "application/json",
          accept: "application/pdf",
          ...(token ? { authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        let msg = `HTTP ${res.status}`;
        try {
          const text = await res.text();
          const j = text ? JSON.parse(text) : null;
          msg = j?.error?.message ?? text ?? msg;
        } catch {
          // ignore
        }
        throw new Error(msg);
      }

      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      // Open in a new tab — operator decides print / save.  Revoke
      // shortly after so memory stays clean.
      window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    },
  });

  function patch<K extends keyof FormShape>(k: K, v: FormShape[K]) {
    setF((cur) => ({ ...cur, [k]: v }));
  }

  if (editId && !hydrated) {
    return <p className="text-text-muted">{t("testReportForm.loading")}</p>;
  }

  return (
    <>
      <PageHeader
        eyebrow={t("eyebrows.adminPrint")}
        title={editId ? t("testReportForm.titleEdit") : t("testReportForm.titleNew")}
        description={
          editId
            ? t("testReportForm.descEdit")
            : t("testReportForm.descNew")
        }
      />

      <form
        className="space-y-6"
        onSubmit={(e) => {
          e.preventDefault();
          m.mutate();
        }}
      >
        <fieldset className="card p-4 space-y-3">
          <legend className="text-xs font-mono uppercase tracking-wider text-text-muted px-2">
            {t("testReportForm.batchLegend")}
          </legend>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label={t("testReportForm.article")}>
              <input className={INPUT_MONO} value={f.article} onChange={(e) => patch("article", e.target.value)} />
            </Field>
            <Field label={t("testReportForm.productionDate")}>
              <input type="date" className={INPUT} value={f.production_date} onChange={(e) => patch("production_date", e.target.value)} />
            </Field>
          </div>
          <Field label={t("testReportForm.serials")}>
            <textarea
              rows={6}
              className={`${INPUT_MONO} font-mono`}
              value={f.serials}
              onChange={(e) => patch("serials", e.target.value)}
            />
          </Field>
          <Field label={t("testReportForm.seedSalt")}>
            <input className={INPUT_MONO} value={f.salt} onChange={(e) => patch("salt", e.target.value)} />
          </Field>
        </fieldset>

        <fieldset className="card p-4 space-y-3">
          <legend className="text-xs font-mono uppercase tracking-wider text-text-muted px-2">
            {t("testReportForm.normsLegend")}
          </legend>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Field label={t("testReportForm.flowMin")}><NumField v={f.min_flow} on={(v) => patch("min_flow", v)} /></Field>
            <Field label={t("testReportForm.flowNominal")}><NumField v={f.nominal_flow} on={(v) => patch("nominal_flow", v)} /></Field>
            <Field label={t("testReportForm.flowTolerance")}><NumField v={f.flow_tolerance} on={(v) => patch("flow_tolerance", v)} /></Field>
            <Field label={t("testReportForm.selMin")}><NumField v={f.min_selectivity} on={(v) => patch("min_selectivity", v)} /></Field>
            <Field label={t("testReportForm.selNominal")}><NumField v={f.nominal_selectivity} on={(v) => patch("nominal_selectivity", v)} /></Field>
          </div>
        </fieldset>

        <fieldset className="card p-4 space-y-3">
          <legend className="text-xs font-mono uppercase tracking-wider text-text-muted px-2">
            {t("testReportForm.conditionsLegend")}
          </legend>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <Field label={t("testReportForm.pressureBar")}><NumField v={f.test_pressure} on={(v) => patch("test_pressure", v)} /></Field>
            <Field label={t("testReportForm.naclMgl")}><NumField v={f.salt_nacl} on={(v) => patch("salt_nacl", v)} /></Field>
            <Field label={t("testReportForm.tempC")}><NumField v={f.test_temperature} on={(v) => patch("test_temperature", v)} /></Field>
            <Field label={t("testReportForm.recovery")}><NumField v={f.recovery} on={(v) => patch("recovery", v)} /></Field>
            <Field label="pH"><NumField v={f.ph} on={(v) => patch("ph", v)} /></Field>
            <Field label={t("testReportForm.activeArea")}><NumField v={f.active_area} on={(v) => patch("active_area", v)} /></Field>
          </div>
        </fieldset>

        <fieldset className="card p-4 space-y-3">
          <legend className="text-xs font-mono uppercase tracking-wider text-text-muted px-2">
            {t("testReportForm.limitsLegend")}
          </legend>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <Field label={t("testReportForm.pMax")}><NumField v={f.max_working_pressure} on={(v) => patch("max_working_pressure", v)} /></Field>
            <Field label={t("testReportForm.tMax")}><NumField v={f.max_temperature} on={(v) => patch("max_temperature", v)} /></Field>
            <Field label={t("testReportForm.freeCl")}><NumField v={f.free_chlorine_max} on={(v) => patch("free_chlorine_max", v)} /></Field>
          </div>
        </fieldset>

        <fieldset className="card p-4 space-y-3">
          <legend className="text-xs font-mono uppercase tracking-wider text-text-muted px-2">
            {t("testReportForm.certHeaderLegend")}
          </legend>
          <Field label={t("testReportForm.recipient")}>
            <textarea rows={5} className={INPUT} value={f.customer} onChange={(e) => patch("customer", e.target.value)} />
          </Field>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label={t("testReportForm.docNumber")}><input className={INPUT_MONO} value={f.doc_number} onChange={(e) => patch("doc_number", e.target.value)} /></Field>
            <Field label={t("testReportForm.docDate")}><input type="date" className={INPUT} value={f.doc_date} onChange={(e) => patch("doc_date", e.target.value)} /></Field>
            <Field label={t("testReportForm.standard")}><input className={INPUT} value={f.doc_standard} onChange={(e) => patch("doc_standard", e.target.value)} /></Field>
            <Field label={t("testReportForm.issuer")}><input className={INPUT} value={f.issuer} onChange={(e) => patch("issuer", e.target.value)} /></Field>
            <Field label={t("testReportForm.contact")}><input className={INPUT} value={f.issuer_contact} onChange={(e) => patch("issuer_contact", e.target.value)} /></Field>
          </div>
        </fieldset>

        {m.isError && (
          <ErrorBanner onDismiss={() => m.reset()}>
            {(m.error as Error).message}
          </ErrorBanner>
        )}

        <div className="flex justify-end">
          <button type="submit" className="btn btn-primary" disabled={m.isPending}>
            {m.isPending
              ? editId
                ? t("common.saving")
                : t("testReportForm.generating")
              : editId
                ? t("testReportForm.saveAndPrint")
                : t("testReportForm.generatePdf")}
          </button>
        </div>
      </form>
    </>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-xs font-mono uppercase tracking-wider text-text-muted">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function NumField({ v, on }: { v: string; on: (v: string) => void }) {
  return (
    <input
      type="number"
      step="any"
      inputMode="decimal"
      className={INPUT_MONO}
      value={v}
      onChange={(e) => on(e.target.value)}
    />
  );
}
